import { maybe, rd } from '@passionware/monads';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { IntegrationDto } from 'dtos/integration.dto';
import { ReduxProxy } from 'v5/api/_common/reduxProxy';
import {
  Integration,
  IntegrationApi
} from 'v5/api/integration/integration.api';
import { ensureIdleQuery } from 'v5/platform/ensureIdleQuery';
import { IntegrationService } from './IntegrationService';

/**
 * v5 read of the integrations that Redux loads at sign-in (V1-1885; same shape as
 * EnterpriseFeatureFlagService, V1-1803): the cache is seeded from the Redux
 * actions, so the app makes no extra request.
 *
 * Integrations are organisation-wide today. V1-2030 makes them per workspace,
 * visitor management first (workspace row, else the global one), and V1-1997
 * moves DocuSign the same way — extend this service with the workspace there.
 */
export function createIntegrationService(config: {
  api: IntegrationApi;
  queryClient: QueryClient;
  reduxProxy: Pick<ReduxProxy, 'read'>;
  resolveOrganizationId: () => string | undefined;
}): IntegrationServiceInstance {
  const { api, queryClient, reduxProxy, resolveOrganizationId } = config;

  const integrationsQueryKey = (organizationId: string) =>
    ['integrations-v5', organizationId] as const;

  const toIntegration = ({ id, enabled }: IntegrationDto): Integration => ({
    id,
    enabled
  });

  // The session is read only for the integration events: every other app action passes by
  // too, some after the page (or a test's DOM) is gone.
  const setIntegrations = (
    update: (
      integrations: Integration[] | undefined
    ) => Integration[] | undefined
  ) => {
    const organizationId = resolveOrganizationId();
    if (organizationId) {
      queryClient.setQueryData<Integration[]>(
        integrationsQueryKey(organizationId),
        update
      );
    }
  };

  const unsubscribe = reduxProxy.read.addListener(event => {
    switch (event.type) {
      case 'integrations/getIntegrations/fulfilled':
        setIntegrations(() => event.payload.map(toIntegration));
        return;
      case 'integrations/configureIntegration/fulfilled': {
        const saved = toIntegration(event.payload);
        setIntegrations(
          integrations =>
            integrations && [
              ...integrations.filter(
                integration => integration.id !== saved.id
              ),
              saved
            ]
        );
        return;
      }
    }
  });

  const service: IntegrationService = {
    useIntegration: (organizationId, id) =>
      rd.map(
        ensureIdleQuery(
          organizationId,
          useQuery(
            {
              enabled: maybe.isPresent(organizationId),
              queryKey: integrationsQueryKey(organizationId!),
              queryFn: api.getIntegrations,
              staleTime: Infinity
            },
            queryClient
          )
        ),
        integrations =>
          maybe.getOrThrow(
            integrations.find(integration => integration.id === id),
            `Integration ${id} is not configured`
          )
      )
  };

  return Object.assign(service, { dispose: unsubscribe });
}

export type IntegrationServiceInstance = IntegrationService & {
  dispose: () => void;
};
